import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { formatMinor, toMajor, toMinor, type Vendor } from '@eprintsmart/shared';
import {
  approveSettlement, getVendorSettlement, markSettlementPaid,
  type VendorSettlementView,
} from '../services/orders';

/**
 * One vendor's monthly statement.
 *
 * The callables have been deployed for a long time with nothing calling them,
 * so paying a print partner meant editing Firestore by hand — the only place
 * on the platform where real money moved through a manual database write.
 *
 * Two steps, as with the artist statement: approving freezes what is owed,
 * marking paid records that the transfer actually left the bank. Collapsing
 * them would make a statement nobody transferred look identical to one they
 * did.
 *
 * Deliberately NOT shared with SettlementDialog. The two look alike and differ
 * where it counts: a vendor is billed against dispatched jobs and their own
 * rate card, an artist against royalty on sold designs, and the deduction
 * defaults differ because TDS on a printing service is not TDS on a royalty.
 * Merging them would mean a component full of `if (isVendor)`, and the first
 * divergence would be applied to the wrong side of the business.
 */

const currentPeriod = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

export function VendorSettlementDialog({
  vendor,
  onClose,
}: {
  vendor: Vendor | null;
  onClose: () => void;
}) {
  const [period, setPeriod] = useState(currentPeriod());
  const [view, setView] = useState<VendorSettlementView | null>(null);
  const [deductions, setDeductions] = useState('0');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!vendor) return;
    setBusy(true);
    setError(null);
    try {
      const next = await getVendorSettlement({ vendorId: vendor.id, period });
      setView(next);
      if (next.settlement) {
        setDeductions(String(toMajor(next.settlement.deductionsMinor)));
        setNote(next.settlement.note ?? '');
      } else {
        // Nothing offered by default, unlike the artist statement.
        //
        // A royalty attracts TDS at a predictable 10%; a printing bill is a
        // supplier invoice whose treatment depends on the contract, the
        // vendor's GST registration and whether 194C applies at all. Guessing
        // a number here would be guessing at somebody's tax position.
        setDeductions('0');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load that statement.');
    } finally {
      setBusy(false);
    }
  }, [vendor, period]);

  useEffect(() => { void load(); }, [load]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  if (!vendor) return null;

  const frozen = view?.settlement;
  const paid = frozen?.status === 'PAID';
  const gross = frozen?.grossMinor ?? view?.grossMinor ?? 0;
  const deductionsMinor = toMinor(Number(deductions) || 0);
  const net = frozen ? frozen.netPayableMinor : gross - deductionsMinor;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{vendor.name} — statement</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/*
          * There is no bill to pay ourselves.
          *
          * An in-house job is costed at zero by assignVendor for exactly this
          * reason, so a statement here would always be empty — saying so beats
          * showing a blank table that looks like a bug.
          */}
        {vendor.isInHouse && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This is the in-house shop, not a supplier. Its work is costed at zero because there is
            no bill to pay ourselves, so this statement will be empty.
          </Alert>
        )}

        {!vendor.gstin && !vendor.isInHouse && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No GSTIN on file for {vendor.name}. Worth having before you pay a supplier invoice.
          </Alert>
        )}

        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <TextField label="Month" size="small" value={period} disabled={busy}
            onChange={(e) => setPeriod(e.target.value)} placeholder="2026-08" sx={{ width: 140 }} />
          {frozen && (
            <Typography variant="body2" color={paid ? 'success.main' : 'text.secondary'}>
              {paid
                ? `Paid ${frozen.paidAt ? new Date(frozen.paidAt).toLocaleDateString('en-IN') : ''}`
                : 'Approved and frozen — the lines below cannot change now'}
            </Typography>
          )}
        </Stack>

        {view && view.lines.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing dispatched in that month. A job only counts once it has actually gone out.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Product</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell>Dispatched</TableCell>
                <TableCell align="right">Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {view?.lines.map((l) => (
                <TableRow key={l.jobId}>
                  <TableCell>{l.orderNumber}</TableCell>
                  <TableCell>{l.productName}</TableCell>
                  <TableCell align="right">{l.qty}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {l.dispatchedAt
                        ? new Date(l.dispatchedAt).toLocaleDateString('en-IN')
                        : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{formatMinor(l.amountMinor)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Box mt={3}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <TextField
              label="Deductions" size="small" value={deductions}
              disabled={busy || Boolean(frozen)}
              onChange={(e) => setDeductions(e.target.value)} sx={{ width: 180 }}
              helperText="TDS, damages, agreed credits — recorded, not netted off silently"
            />
            <TextField
              label="Note to the vendor" size="small" value={note}
              disabled={busy || Boolean(frozen)}
              onChange={(e) => setNote(e.target.value)} fullWidth
              helperText="Why the transfer is smaller than the total"
            />
          </Stack>

          <Stack direction="row" spacing={4} mt={2}>
            <Typography variant="body2">
              Jobs <strong>{frozen?.jobCount ?? view?.lines.length ?? 0}</strong>
            </Typography>
            <Typography variant="body2">Billed <strong>{formatMinor(gross)}</strong></Typography>
            <Typography variant="body2">
              Less deductions <strong>{formatMinor(frozen?.deductionsMinor ?? deductionsMinor)}</strong>
            </Typography>
            <Typography variant="body2">To transfer <strong>{formatMinor(net)}</strong></Typography>
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!frozen && (
          <Button variant="outlined" disabled={busy || !view?.lines.length}
            onClick={() => void run(() => approveSettlement({
              vendorId: vendor.id, period, deductionsMinor, note: note || undefined,
            }))}>
            Approve
          </Button>
        )}
        {frozen && !paid && (
          <Button variant="contained" disabled={busy}
            onClick={() => void run(() => markSettlementPaid({ vendorId: vendor.id, period }))}>
            I have paid {formatMinor(net)}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
